import { ApolloError } from '@apollo/client'
import { Spin } from 'antd'
import { FC, useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MeetingType,
  MeetingStepViewId,
  SkipMeetingStepReason,
  EmployeeDisplayTinyInfo,
  EmployeeWithBuFragment,
  AppraisalSeasonDto,
  EmployeeWithBusinessUnit,
  MeetingInviteOptionsExtended,
} from '../../../types'
import { useAuth } from '../../../context/auth-context'
import useInviteMeeting, { MeetingInviteProps } from '../../../graphql/meeting/hooks/useInviteMeeting'
import { ValidationErrorsData } from '../../../shared/graphqlErrorHelper'
import { PerModal } from '../../../components/custom_components/Modal'
import { StatusMessageBox } from '../../../components/custom_components/StatusMessageBox'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { getSampleEmail, getNextStepConfiguration, fullAppraisalSteps, updateAppraisalSteps } from '../../meeting/invite/inviteUtil'
import moment from 'moment-timezone'
import { sortSteps } from '../../appraisal/appraisal-flow/meeting/stepSorter'
import { InviteModalContentUtils } from '../../../components/custom_components/InviteModalContent/inviteModalContentUtils'
import DashboardAppraisalInviteModalContent from './DashboardAppraisalInviteModalContent'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import { getQueryErrorMessage } from './AppraisalInviteModal'

export interface DashboardAppraisalInviteModalState {
  meetingDate: string
  meetingType: MeetingType
  appraisalSeason: AppraisalSeasonDto
  emailText?: string
  viewIds?: MeetingStepViewId[]
  reasonForReducedSteps?: SkipMeetingStepReason
  serverError?: ValidationErrorsData[]
  location?: string
  duration: number
  additionalParticipants: EmployeeWithBuFragment[]
  additionalParticipantsOptions: EmployeeWithBuFragment[]
}

interface ReducerActionType {
  type: 'update' | 'updateSteps' | 'reset' | 'setError'
  fieldName?: string
  value?: unknown
  stepId?: MeetingStepViewId
  error?: ValidationErrorsData[]
  serverError?: null
  location?: string
}

interface DashboardAppraisalInviteModalProps {
  loading: boolean
  queryError?: ApolloError
  onCancel: () => void
  employee?: EmployeeDisplayTinyInfo | null
  appraisalSeason?: AppraisalSeasonDto | null
  additionalParticipantOptions?: EmployeeWithBusinessUnit[]
  meetingInviteOptions?: MeetingInviteOptionsExtended | null
}

export const DashboardAppraisalInviteModal: FC<DashboardAppraisalInviteModalProps> = ({
  loading,
  queryError,
  employee,
  onCancel,
  appraisalSeason,
  additionalParticipantOptions,
  meetingInviteOptions,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { createDashboardAppraisalInvite } = useInviteMeeting()
  const [isSubmitting, toggleSubmitting] = useState(false)

  const possibleMeetingType = meetingInviteOptions
    ? meetingInviteOptions.possible.filter((type) => [MeetingType.Appraisal, MeetingType.AppraisalUpdate].includes(type))
    : []
  const meetingType = possibleMeetingType[0] ?? MeetingType.Appraisal
  const hasQueryData = !!employee && !!appraisalSeason && !!additionalParticipantOptions && !!meetingInviteOptions
  const canInvite =
    hasQueryData &&
    !!possibleMeetingType.length &&
    meetingInviteOptions.isAppraisalEnabled &&
    !meetingInviteOptions.tagAssignmentIsInProgress
  const queryFailed = !loading && (!!queryError || !hasQueryData)

  let suggestedDate = moment().tz(user.timezone).startOf('isoWeek').add(1, 'week').hours(8)
  if (appraisalSeason?.toDate && suggestedDate.isAfter(appraisalSeason.toDate)) {
    suggestedDate = moment(appraisalSeason.toDate).tz(user.timezone).subtract(1, 'days').startOf('day').hours(8)
    if (suggestedDate.isoWeekday() === 6 || suggestedDate.isoWeekday() === 7) {
      suggestedDate = suggestedDate.isoWeekday(5)
    }
  }

  const suggestedDateUTCString = suggestedDate.tz('UTC').format('YYYY-MM-DDTHH:mm:00.000') + 'Z'
  const placeholderEmployee: EmployeeDisplayTinyInfo = employee ?? { id: 0, firstName: '', lastName: '' }
  const placeholderSeason: AppraisalSeasonDto = appraisalSeason ?? {
    id: 0,
    companyId: 0,
    country: '',
    year: 0,
    fromDate: suggestedDateUTCString,
  }

  const initialState: DashboardAppraisalInviteModalState = {
    meetingDate: suggestedDateUTCString,
    serverError: undefined,
    emailText: getSampleEmail(t, meetingType, placeholderEmployee, user),
    viewIds: meetingType === MeetingType.Appraisal ? fullAppraisalSteps : updateAppraisalSteps,
    appraisalSeason: placeholderSeason,
    meetingType,
    reasonForReducedSteps: undefined,
    location: undefined,
    duration: 3600,
    additionalParticipantsOptions: additionalParticipantOptions ?? [],
    additionalParticipants: [],
  }

  const reducer = (state: DashboardAppraisalInviteModalState, action: ReducerActionType) => {
    const nextState = { ...state }
    switch (action.type) {
      case 'reset':
        nextState.serverError = []
        toggleSubmitting(false)
        return initialState

      case 'update':
        const { fieldName, value } = action
        if (fieldName) {
          nextState[fieldName] = value
        }
        break

      case 'updateSteps':
        nextState.viewIds = getNextStepConfiguration(action.stepId!, state.meetingType, state.viewIds)
        break

      case 'setError':
        if (action.error) {
          nextState.serverError = action.error as ValidationErrorsData[]
        }
        break

      default:
        throw new Error()
    }
    return nextState
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    if (canInvite) {
      dispatch({ type: 'reset' })
    }
    // Reset form state once invite options are available for this employee.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canInvite, employee?.id])

  const containsStepViewId = (stepId: MeetingStepViewId): boolean => {
    if (state.viewIds) {
      return !!state.viewIds.find((x) => x === stepId)
    }
    return false
  }

  const isAppraisalMeeting = state.meetingType === MeetingType.Appraisal
  const isAppraisalUpdateMeeting = state.meetingType === MeetingType.AppraisalUpdate

  const appraisalWithReducedSteps =
    isAppraisalMeeting && !(containsStepViewId(MeetingStepViewId.SelfReflection) && containsStepViewId(MeetingStepViewId.Evaluation))

  const saveInvitation = async () => {
    if (!employee || !appraisalSeason) {
      return
    }
    toggleSubmitting(true)
    const sanitizedLocation = !!state.location ? (state.location.trim().length > 0 ? state.location.trim() : undefined) : undefined
    const inviteData: MeetingInviteProps = {
      employeeId: employee.id,
      appraisalSeasonId: state.appraisalSeason!.id,
      reviewerId: user.employeeId,
      meetingDate: state.meetingDate,
      meetingType: state.meetingType,
      viewIds: sortSteps(state.viewIds ? state.viewIds : [], state.meetingType === MeetingType.AppraisalUpdate),
      emailText: state.emailText,
      reasonForReducedSteps: appraisalWithReducedSteps ? state.reasonForReducedSteps : undefined,
      location: sanitizedLocation,
      duration: state.duration,
      additionalParticipantsIds: state.additionalParticipants.map((e) => e.id),
    }

    const result = await createDashboardAppraisalInvite(inviteData, appraisalSeason.companyId)

    if (result && !result.errors) {
      showNotification({
        type: 'success',
        message: t(`meetingInvite.notifications.invite.success.title`),
        description: t(`meetingInvite.notifications.invite.success.description`, {
          employeeName: employee.firstName,
        }),
      })
      dispatch({ type: 'reset' })
      onCancel()
    } else {
      toggleSubmitting(false)
      dispatch({
        type: 'setError',
        error: result.errors && result.errors[0] && result.errors[0].extensions ? result.errors[0].extensions.validationErrors : undefined,
      })
    }
  }

  const invalidAppraisal = appraisalWithReducedSteps && !state.reasonForReducedSteps
  const invalidAppraisalUpdate = isAppraisalUpdateMeeting && (!state.viewIds || state.viewIds.length === 0)

  const inviteModalContentUtils = new InviteModalContentUtils({
    timeZoneAndLocale: user,
    appraisalSeason: appraisalSeason ?? undefined,
  })
  const invalidDateTime =
    state.meetingDate === '' ||
    state.meetingDate === null ||
    state.meetingDate === undefined ||
    inviteModalContentUtils.isMeetingDateNotAllowed(state.meetingDate)

  const disabledInviteButton =
    invalidAppraisalUpdate || invalidAppraisal || isSubmitting || invalidDateTime || (state.serverError && state.serverError.length > 0)

  const disabledInviteButtonTooltip = invalidDateTime ? t(`meetingInvite.invalidDateTimeTooltip`) : undefined
  const isSkillStatusBlocker = false

  const closeModal = () => {
    if (canInvite) {
      dispatch({ type: 'reset' })
    }
    onCancel()
  }

  const closeFooter = {
    justify: 'start' as const,
    customBtns: [
      {
        onClick: closeModal,
        text: t(`global.button.close`),
        color: 'secondary' as const,
        icon: 'close_stroke' as const,
      },
    ],
  }

  const inviteFooter = {
    justify: 'end' as const,
    customBtns: [
      {
        text: t(`meetingInvite.doScheduleButton`),
        onClick: async (e) => {
          e.preventDefault()
          await saveInvitation()
        },
        color: 'primary' as const,
        icon: 'calendar_fill' as const,
        disabled: disabledInviteButton || isSkillStatusBlocker,
        isProcessing: { active: isSubmitting },
        tooltip: isSkillStatusBlocker ? t('meetingInvite.unknownSkillStatus') : disabledInviteButtonTooltip,
        visible: !state.serverError,
        dataE2eAttr: 'invite',
      },
    ],
  }

  let body: React.ReactNode
  if (loading) {
    body = (
      <div style={{ height: '50px' }}>
        <PerFlexRow mt="l" align="middle" justify="center">
          <Spin size="large" spinning />
        </PerFlexRow>
      </div>
    )
  } else if (queryFailed) {
    body = <StatusMessageBox variant="error" message={getQueryErrorMessage(queryError, t('global.notifications.error.description'))} />
  } else if (!canInvite && employee) {
    body = (
      <PerText>
        {t(`meetingInvite.noAppraisalAvailable`, {
          employeeFirstName: employee.firstName,
          meetingType: possibleMeetingType.length
            ? t(`meetingTypes.${possibleMeetingType[0]}`)
            : t(`meetingTypes.${MeetingType.Appraisal}`),
        })}
      </PerText>
    )
  } else if (employee && appraisalSeason) {
    body = (
      <DashboardAppraisalInviteModalContent
        employee={employee}
        state={state}
        timelocal={user}
        inviteModalContentUtils={inviteModalContentUtils}
        onUpdate={(fieldName, value) => dispatch({ type: 'update', fieldName: fieldName, value: value })}
        onUpdateSteps={(stepId, value) => dispatch({ type: 'updateSteps', stepId: stepId, value: value })}
        requestedMeeting={undefined}
        serverError={state.serverError}
      />
    )
  }

  return (
    <PerModal
      visible
      onCancel={closeModal}
      size="large"
      title={{
        heading: t(`meetingInvite.headline`),
      }}
      footer={canInvite ? inviteFooter : closeFooter}
    >
      {body}
    </PerModal>
  )
}
